import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeSlug, setActiveSlug] = useState(slug || 'travel');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
    window.scrollTo(0, 0);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = getArticlesByCategory(activeSlug);

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    navigate(`/category/${newSlug}`);
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-100 border-b border-background-200/70" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
              <li className="text-foreground-300">/</li>
              <li className="text-foreground-900 font-medium">{category.name}</li>
            </ol>
          </div>
        </nav>

        <section className="relative h-60 md:h-80 overflow-hidden">
          <img
            src={category.image}
            alt={`蘑菇社区${category.name}兴趣圈子`}
            title={`蘑菇社区${category.name}兴趣圈子`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-white/90 flex items-center justify-center">
                <i className={`${category.icon} text-xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">{category.name}</h1>
                <p className="text-sm text-white/70">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  {category.name} · 全部文章
                </h2>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {categoryArticles.length} 篇文章
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span><strong className="text-foreground-700">{category.memberCount.toLocaleString()}</strong> 成员</span>
                <span><strong className="text-foreground-700">{category.articleCount.toLocaleString()}</strong> 文章</span>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`蘑菇社区文章 - ${article.title}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-base text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-400">
                      <div className="flex items-center gap-2">
                        <img src={article.authorAvatar} alt={article.author} className="w-5 h-5 rounded-full object-cover" />
                        <span>{article.author}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><i className="ri-heart-line"></i> {article.likes.toLocaleString()}</span>
                        <span className="flex items-center gap-1"><i className="ri-chat-1-line"></i> {article.comments}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}